/**
 * Head management without a dependency.
 *
 * React 19 can hoist <title> and <meta> rendered inside a component, but it
 * does not de-duplicate tags that already exist in index.html, which would
 * leave two descriptions in the document. Setting them imperatively keeps
 * exactly one of each and keeps the fallbacks in index.html meaningful for
 * crawlers that do not execute JavaScript.
 *
 * One consequence worth knowing: this is a client-rendered site, so everything
 * below is applied *after* the JavaScript runs. Googlebot renders and sees it;
 * plenty of other crawlers do not, and take whatever index.html declares. That
 * is why index.html carries a complete, honest set of home-page tags plus the
 * site-wide JSON-LD graph, and why any change here should be mirrored there if
 * it affects the home page. Per-route tags for non-rendering crawlers would
 * need prerendering - see README.
 */

import { useEffect } from 'react'
import { SITE_URL, company, routeLabels, seoDefaults } from '../data/site'

/**
 * Strip soft hyphens.
 *
 * Several headings carry U+00AD at morpheme boundaries so
 * German compounds break correctly on a phone. They are invisible on screen but
 * are real characters in the string, and would end up inside the structured
 * data and the page title as mojibake in anything that does not handle them.
 */
export function plain(text) {
  return typeof text === 'string' ? text.replace(/\u00ad/g, '') : text
}

/**
 * Turn a route path into an absolute canonical URL.
 *
 * The root keeps its trailing slash (`https://.../`) and every other path drops
 * one (`https://.../insights`), which is what index.html and the sitemap declare.
 * A canonical that disagrees with the sitemap by one character is treated as a
 * second URL.
 */
export function absoluteUrl(path = '/') {
  if (/^https?:\/\//.test(path)) return path
  if (!path || path === '/') return `${SITE_URL}/`
  return `${SITE_URL}${path.replace(/\/+$/, '')}`
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value)
  }
  return element
}

/**
 * `rel` may repeat (two `alternate` links), so the selector is passed in full
 * and includes whatever distinguishes them.
 */
function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('link')
    document.head.appendChild(element)
  }
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value)
  }
  return element
}

function removeMeta(selector) {
  document.head.querySelector(selector)?.remove()
}

/**
 * @param {object} options
 * @param {string} options.title     Page title, without the brand suffix.
 * @param {string} options.description
 * @param {string} options.path      Route path, e.g. `/leistungen`.
 * @param {boolean} [options.isHome] Home page uses the untemplated title.
 * @param {'website'|'article'} [options.type]
 * @param {boolean} [options.noIndex]
 * @param {string} [options.image]   Route-specific social card, absolute or
 *                                   root-relative. Defaults to the site card.
 * @param {string} [options.imageAlt]
 * @param {object} [options.article] `{ published, modified, section, author }`
 *                                   - emitted as `article:*` for `type:
 *                                   'article'`.
 * @param {string} [options.breadcrumbLabel] Name for the last crumb when the
 *                                   URL cannot supply it (article slugs).
 */
export function useSeo({
  title,
  description,
  path,
  isHome = false,
  type = 'website',
  noIndex = false,
  image,
  imageAlt,
  article,
  breadcrumbLabel,
}) {
  /*
   * `article` is an object literal at the call site, so it is a fresh reference
   * on every render. Comparing its serialised form keeps the effect from
   * re-running for an unchanged value.
   */
  const articleKey = article ? JSON.stringify(article) : ''

  useEffect(() => {
    const fullTitle = isHome
      ? seoDefaults.homeTitle
      : seoDefaults.titleTemplate(plain(title))
    const url = absoluteUrl(path)
    const text = description || seoDefaults.description
    const cardUrl = absoluteUrl(image || seoDefaults.ogImage)
    const cardAlt = imageAlt || seoDefaults.ogImageAlt

    document.title = fullTitle
    document.documentElement.lang = 'de'

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: text,
    })
    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: url })

    /*
     * Self-referencing hreflang. The site exists in German only, so this says
     * exactly that - `de` for German speakers and `x-default` for everyone
     * else - which stops Google guessing that a missing translation exists.
     */
    upsertLink('link[rel="alternate"][hreflang="de"]', {
      rel: 'alternate',
      hreflang: 'de',
      href: url,
    })
    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: 'alternate',
      hreflang: 'x-default',
      href: url,
    })

    // ---- Open Graph ----
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: fullTitle,
    })
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: text,
    })
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url })
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type })
    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: company.brand,
    })
    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: seoDefaults.locale,
    })
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: cardUrl,
    })
    upsertMeta('meta[property="og:image:secure_url"]', {
      property: 'og:image:secure_url',
      content: cardUrl,
    })
    upsertMeta('meta[property="og:image:width"]', {
      property: 'og:image:width',
      content: seoDefaults.ogImageWidth,
    })
    upsertMeta('meta[property="og:image:height"]', {
      property: 'og:image:height',
      content: seoDefaults.ogImageHeight,
    })
    upsertMeta('meta[property="og:image:alt"]', {
      property: 'og:image:alt',
      content: cardAlt,
    })

    // ---- X / Twitter ----
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    })
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: fullTitle,
    })
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: text,
    })
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: cardUrl,
    })
    upsertMeta('meta[name="twitter:image:alt"]', {
      name: 'twitter:image:alt',
      content: cardAlt,
    })

    // ---- Article metadata ----
    // Removed rather than left stale when navigating from an article to a
    // normal page, which would otherwise date the new page to the old post.
    const articleTags = [
      ['article:published_time', article?.published],
      ['article:modified_time', article?.modified || article?.published],
      ['article:section', article?.section],
      ['article:publisher', article ? company.brand : undefined],
    ]
    for (const [property, value] of articleTags) {
      if (type === 'article' && value) {
        upsertMeta(`meta[property="${property}"]`, { property, content: value })
      } else {
        removeMeta(`meta[property="${property}"]`)
      }
    }

    // ---- Indexing ----
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noIndex ? 'noindex, follow' : seoDefaults.robots,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- articleKey stands in for article
  }, [title, description, path, isHome, type, noIndex, image, imageAlt, articleKey])

  /*
   * Breadcrumbs, derived from the path.
   *
   * Emitted here rather than page by page so every route gets a trail without
   * anyone remembering to add one, and so it can never contradict the visual
   * breadcrumb - both come from the same route table. Google uses it to replace
   * the URL line in the result with `casa-hausverwaltung.de > Insights > ...`.
   */
  useEffect(() => {
    if (noIndex) return undefined
    const data = breadcrumbJsonLd(path, breadcrumbLabel ?? plain(title))
    if (!data) return undefined

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.dataset.jsonldId = 'breadcrumb'
    script.textContent = JSON.stringify(data)
    document.head.appendChild(script)
    return () => script.remove()
  }, [path, breadcrumbLabel, title, noIndex])
}

/**
 * Structured data. Rendered as a plain <script> element appended to <head> so
 * it survives route changes cleanly and is removed on unmount.
 *
 * `data` must be referentially stable across renders - define it at module
 * scope or memoise it, or the effect re-runs on every render and the script is
 * torn down and rebuilt each time.
 */
export function useJsonLd(data, id) {
  useEffect(() => {
    if (!data) return undefined
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.dataset.jsonldId = id
    script.textContent = JSON.stringify(data)
    document.head.appendChild(script)
    return () => script.remove()
  }, [data, id])
}

/* ==========================================================================
   Structured-data builders
   --------------------------------------------------------------------------
   Every value below is a documented fact from the Impressum or the site's own
   copy. Nothing is invented: there are no opening hours, no geo-coordinates,
   no aggregate rating and no price range, because none of those are recorded
   anywhere in the source material - and a fabricated rating in particular is
   grounds for a structured-data manual action, not just a wrong snippet.

   The identifiers (`#organisation`, `#website`) are shared with the static
   graph in index.html on purpose, so a crawler merges the two into one entity
   rather than reading them as separate businesses.
   ========================================================================== */

export const ORG_ID = `${SITE_URL}/#organisation`
export const WEBSITE_ID = `${SITE_URL}/#website`

/** LocalBusiness description of CASA. Also emitted statically in index.html. */
export const organisationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'RealEstateAgent',
  '@id': ORG_ID,
  name: company.brand,
  legalName: company.legalName,
  description: seoDefaults.description,
  slogan: company.tagline,
  url: SITE_URL,
  logo: absoluteUrl('/images/logo-on-light.png'),
  image: absoluteUrl(seoDefaults.ogImage),
  telephone: company.phone,
  email: company.email,
  vatID: company.vatId,
  areaServed: 'Aldingen und Umgebung',
  knowsLanguage: 'de-DE',
  address: {
    '@type': 'PostalAddress',
    streetAddress: company.street,
    postalCode: company.postalCode,
    addressLocality: company.city,
    addressCountry: company.countryCode,
  },
  founder: company.managingDirectors.map((name) => ({
    '@type': 'Person',
    name,
  })),
  makesOffer: [
    'WEG-Verwaltung',
    'Sondereigentumsverwaltung',
    'Mietverwaltung',
  ].map((name) => ({
    '@type': 'Offer',
    itemOffered: { '@type': 'Service', name, serviceType: name },
  })),
}

/**
 * BreadcrumbList for a route, derived from its path.
 *
 * `Startseite` is always the first crumb. `extraLabel` names the final segment
 * when the URL cannot supply it - an article slug, for instance.
 */
export function breadcrumbJsonLd(path, extraLabel) {
  if (!path || path === '/') return null

  const segments = path.split('/').filter(Boolean)
  const items = [{ label: 'Startseite', url: absoluteUrl('/') }]

  let accumulated = ''
  segments.forEach((segment, index) => {
    accumulated += `/${segment}`
    const isLast = index === segments.length - 1
    const label =
      routeLabels[accumulated] ?? (isLast && extraLabel ? extraLabel : null)
    // A path segment with no label is a container that has no page of its own;
    // listing it would produce a crumb pointing at a 404.
    if (label) items.push({ label, url: absoluteUrl(accumulated) })
  })

  if (items.length < 2) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.url,
    })),
  }
}

/**
 * FAQPage from the site's FAQ entries.
 *
 * Google reserves FAQ rich results for authoritative government and health
 * sites, so this will not draw the accordion into the result page. It is still
 * worth emitting: it tells the crawler what question each answer belongs to,
 * which is what the AI overview and "people also ask" surfaces read.
 */
export function faqJsonLd(items) {
  if (!items?.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: plain(item.q ?? item.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: plain(item.a ?? item.answer),
      },
    })),
  }
}

/** BlogPosting for an article, wired to the shared organisation entity. */
export function articleJsonLd(post) {
  const url = absoluteUrl(`/insights/${post.slug}`)
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    headline: plain(post.title),
    description: plain(post.excerpt),
    datePublished: post.date,
    dateModified: post.date,
    articleSection: post.category,
    // `body` is an array of content blocks, so the word count comes from their
    // flattened text rather than from a string length.
    // No `wordCount`: an article's text is a component now, so counting it would
    // mean rendering it. `timeRequired` carries the same information for Google.
    timeRequired: post.minutes ? `PT${post.minutes}M` : undefined,
    inLanguage: seoDefaults.language,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    image: absoluteUrl(seoDefaults.ogImage),
    isPartOf: { '@id': WEBSITE_ID },
    author: { '@id': ORG_ID, '@type': 'Organization', name: company.brand },
    publisher: { '@id': ORG_ID, '@type': 'Organization', name: company.brand },
  }
}

/** ContactPage, so the phone number and address are attached to /kontakt. */
export function contactPageJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': `${absoluteUrl('/kontakt')}#page`,
    url: absoluteUrl('/kontakt'),
    inLanguage: seoDefaults.language,
    about: { '@id': ORG_ID },
    mainEntity: {
      '@id': ORG_ID,
      '@type': 'RealEstateAgent',
      name: company.brand,
      telephone: company.phone,
      email: company.email,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: company.phoneHref.replace('tel:', ''),
        email: company.email,
        availableLanguage: 'German',
        areaServed: company.countryCode,
      },
    },
  }
}

/** ItemList of the services, for /verwalten and /leistungen. */
export function serviceListJsonLd(services, path) {
  if (!services?.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${absoluteUrl(path)}#services`,
    itemListElement: services.map((service, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Service',
        name: plain(service.title ?? service.name),
        description: plain(service.text ?? service.tagline ?? service.description),
        serviceType: plain(service.title ?? service.name),
        provider: { '@id': ORG_ID },
        areaServed: 'Aldingen und Umgebung',
      },
    })),
  }
}
